/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Divider, Form, Grid, Icon } from '@mairistem/composants';
import { NaissanceForm } from '@mairistem/adresses';
import {
  NationaliteInput,
  EtatCivil,
  TypeComponent,
} from '@mairistem/administres-utils';

import type { Adresse, Individu, Sexe } from '@/types';
import { formatAdresse, validationInformationsNaissance } from '@/utils';

import type { TypeDateNaissance } from '@/enums';
import PanelModalEdition from './PanelModalEdition';

interface InformationIndividuModalProps {
  minuteNaissance: number;
  heureNaissance: number;
  jourNaissance: number;
  moisNaissance: number;
  anneeNaissance: number;
  typeDateNaissance: TypeDateNaissance;
  sexe: Sexe;
  nomFamille: string;
  nomUsage: string;
  prenom: string;
  prenomsComplementaires: string;
  adresseNaissance: Partial<Adresse>;
  identifiantNationalite: number;
  parent: boolean;
  title: string;
  updated: boolean;
  filiationInconnue?: boolean;
  dateRecensement?: string;
  dateNaissanceRecense?: {
    anneeNaissance: number;
    moisNaissance: number;
    jourNaissance: number;
  };
  onFiliationInconnueChange: (isUnknown: boolean) => void;
  onSave: () => Promise<void>;
  onChange: (data: Partial<Individu>) => void;
  onClose: () => void;
  onCancel: () => void;
}

const InformationIndividuModal = (props: InformationIndividuModalProps) => {
  const {
    minuteNaissance,
    heureNaissance,
    jourNaissance,
    moisNaissance,
    anneeNaissance,
    typeDateNaissance,
    sexe,
    nomFamille,
    nomUsage,
    prenom,
    prenomsComplementaires,
    adresseNaissance,
    identifiantNationalite,
    parent,
    title,
    updated,
    filiationInconnue,
    dateRecensement,
    dateNaissanceRecense,
    onFiliationInconnueChange,
    onChange,
    onSave,
    onClose,
    onCancel,
  } = props;

  const [loading, setLoading] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<
    Partial<Individu> & { api?: boolean }
  >({});

  const handleChange = React.useCallback(
    (_e: React.FormEvent<HTMLInputElement>, data: any) => {
      onChange({
        [data.name]: data.checked ?? data.value,
      });
    },
    [onChange],
  );

  const handleAdresseChange = React.useCallback(
    (adresse) => {
      onChange({
        adresseNaissance: formatAdresse(adresseNaissance, adresse, true),
      });
    },
    [onChange, adresseNaissance],
  );

  const handleSave = React.useCallback(() => {
    if (filiationInconnue) {
      setLoading(true);

      onSave()
        .then(() => {
          onClose();
        })
        .catch(() => {
          setErrors({ api: true });
        })
        .finally(() => {
          setLoading(false);
        });
      return;
    }

    const e = validationInformationsNaissance(
      {
        sexe,
        nomFamille,
        nomUsage,
        prenom,
        prenomsComplementaires,
        heureNaissance,
        minuteNaissance,
        jourNaissance,
        moisNaissance,
        anneeNaissance,
        adresseNaissance,
        dateRecensement,
        identifiantNationalite,
      },
      parent,
      filiationInconnue,
      dateNaissanceRecense,
    );

    setErrors(e);

    if (_.isEmpty(e)) {
      setLoading(true);

      onSave()
        .then(() => {
          onClose();
        })
        .catch(() => {
          setErrors({ api: true });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [
    adresseNaissance,
    anneeNaissance,
    dateRecensement,
    dateNaissanceRecense,
    filiationInconnue,
    heureNaissance,
    identifiantNationalite,
    jourNaissance,
    minuteNaissance,
    moisNaissance,
    nomFamille,
    nomUsage,
    onClose,
    onSave,
    parent,
    prenom,
    prenomsComplementaires,
    sexe,
  ]);

  return (
    <PanelModalEdition
      errors={errors}
      loading={loading}
      header={title}
      updated={updated}
      onSave={handleSave}
      onClose={onCancel}
    >
      <Grid as={Form}>
        <Grid.Row>
          <Grid.Column width={3}>
            <Icon name="user" size="massive" />
          </Grid.Column>
          <Grid.Column width={13}>
            <EtatCivil
              typeComponent={TypeComponent.RECENSEMENT}
              disabled={loading}
              onChange={handleChange}
              isParent={parent}
              isFiche
              filiationInconnue={filiationInconnue}
              onFiliationInconnueChange={onFiliationInconnueChange}
              individu={{
                sexe,
                nomFamille,
                nomUsage,
                prenom,
                prenomsComplementaires: prenomsComplementaires
                  ?.split(',')
                  .map((p) => p.trim())
                  .filter(Boolean)
                  .join(', '),
                heureNaissance,
                minuteNaissance,
                jourNaissance,
                moisNaissance,
                anneeNaissance,
                typeDateNaissance,
              }}
            />
            <Divider horizontal className="required">
              Informations de naissance
            </Divider>
            <NaissanceForm
              disabled={filiationInconnue}
              adresse={{
                ...adresseNaissance,
                commune: adresseNaissance?.libelleCommune,
                codePostal: adresseNaissance?.libelleCodePostal,
                pays: adresseNaissance?.libellePays,
                codeDepartement: adresseNaissance?.codeDepartement,
                departement: adresseNaissance?.libelleDepartement,
                identifiantCommune: null,
              }}
              onAdresseChange={handleAdresseChange}
              error={errors?.adresseNaissance}
            />
            {parent && (
              <NationaliteInput
                disabled={filiationInconnue}
                required
                identifiantNationalite={identifiantNationalite}
                onChange={handleChange}
                label="Nationalité"
                // @ts-expect-error style
                style={{ marginTop: '1em' }}
              />
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </PanelModalEdition>
  );
};

export default InformationIndividuModal;
